import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Clock } from 'lucide-react';
import { api } from '../lib/api';
import SEO from '../components/SEO';
import { PILLARS, getPillarForPost } from '../data/pillarsData';

const PILLAR_COLORS = {
  'Precision Weight Management & Metabolic Health': 'bg-amber-50 text-amber-700',
  'Longevity & Biological Age': 'bg-emerald-50 text-emerald-700',
  'Hormones Across the Life Course': 'bg-violet-50 text-violet-700',
  'Microbiome, Genetics & Multi-Omics': 'bg-sky-50 text-sky-700',
  'Aesthetics with a Physician\u2019s Perspective': 'bg-rose-50 text-rose-700',
  "Aesthetics with a Physician's Perspective": 'bg-rose-50 text-rose-700',
  'Functional & Lifestyle Medicine': 'bg-stone-100 text-stone-700',
};

const COPY = {
  en: {
    metaTitle: 'Physician-Written Articles',
    metaDesc: 'Physician-written articles on aesthetics, longevity, hormones, GLP-1, and precision health — evidence-based perspectives from Akira Signature Care.',
    eyebrow: 'INSIGHTS & STORIES',
    heading: 'Articles',
    lede: 'Evidence-based perspectives on medical aesthetics, longevity science, and precision health — written by our physicians.',
    browseByTopic: 'Browse by Topic',
    loading: 'Loading...',
    loadError: 'Couldn\u2019t load posts',
    empty: 'New articles are on the way.',
    minRead: 'min read',
    langSwitchLabel: 'Leer en Español',
    langSwitchTo: '/es/articles',
    path: '/articles',
    articleBase: '/articles',
  },
  es: {
    metaTitle: 'Art\u00edculos escritos por m\u00e9dicos',
    metaDesc: 'Art\u00edculos escritos por m\u00e9dicos sobre est\u00e9tica, longevidad, hormonas, GLP-1 y salud de precisi\u00f3n — perspectivas basadas en evidencia de Akira Signature Care.',
    eyebrow: 'PERSPECTIVAS E HISTORIAS',
    heading: 'Art\u00edculos',
    lede: 'Perspectivas basadas en evidencia sobre est\u00e9tica m\u00e9dica, ciencia de la longevidad y salud de precisi\u00f3n — escritas por nuestras m\u00e9dicas.',
    browseByTopic: 'Explorar por tema',
    loading: 'Cargando...',
    loadError: 'No se pudieron cargar los art\u00edculos',
    empty: 'Pronto habr\u00e1 art\u00edculos nuevos.',
    minRead: 'min de lectura',
    langSwitchLabel: 'Read in English',
    langSwitchTo: '/articles',
    path: '/es/articles',
    articleBase: '/es/articles',
  },
};

const BlogPage = ({ lang = 'en' }) => {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);
  const t = COPY[lang] || COPY.en;

  useEffect(() => {
    api(`/api/blog?lang=${lang}`)
      .then(setPosts)
      .catch((e) => setError(e.message));
  }, [lang]);

  return (
    <div className="min-h-screen bg-stone-50" data-testid={lang === 'es' ? 'articles-page-es' : 'articles-page'}>
      <SEO
        title={t.metaTitle}
        description={t.metaDesc}
        path={t.path}
        lang={lang}
        alternates={[{ hreflang: lang === 'es' ? 'en' : 'es', href: lang === 'es' ? '/articles' : '/es/articles' }]}
        schema={posts && posts.length ? {
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: lang === 'es' ? 'Art\u00edculos de Akira Signature Care' : 'Akira Signature Care Articles',
          inLanguage: lang === 'es' ? 'es' : 'en-US',
          publisher: { '@id': 'https://akirasignaturecare.com/#clinic' },
          blogPost: posts.map((p) => {
            const isHttpUrl = typeof p.cover_image === 'string' && /^https?:\/\//i.test(p.cover_image);
            return {
              '@type': 'BlogPosting',
              headline: p.title,
              url: `https://akirasignaturecare.com${t.articleBase}/${p.slug}`,
              datePublished: p.created_at,
              ...(isHttpUrl ? { image: p.cover_image } : {}),
              author: {
                '@type': 'Person',
                name: p.author_name || 'Akira Signature Care',
              },
            };
          }),
        } : null}
      />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-rose-300 text-sm mb-4">
              <Sparkles className="w-4 h-4" />
              <span>{t.eyebrow}</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl mb-6" data-testid="articles-page-title">
              {t.heading}
            </h1>
            <p className="text-xl text-stone-300 leading-relaxed">
              {t.lede}
            </p>
            <Link
              to={t.langSwitchTo}
              data-testid="lang-switch-link"
              className="inline-flex items-center gap-2 mt-6 text-rose-300 hover:text-white text-xs tracking-[0.3em] uppercase border-b border-rose-300/40 hover:border-white pb-1 transition-colors"
            >
              {t.langSwitchLabel}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          {/* Topic pillars — internal-link surface for crawlers + UX filter (EN only) */}
          {lang === 'en' && (
            <div className="mb-14" data-testid="pillar-chips">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-rose-400" />
                <span className="text-rose-400 text-[11px] tracking-[0.4em] uppercase font-light">
                  {t.browseByTopic}
                </span>
              </div>
              <div className="flex flex-wrap gap-3">
                {PILLARS.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/articles/pillar/${p.slug}`}
                    data-testid={`pillar-chip-${p.slug}`}
                    className={`group inline-flex items-center gap-2 text-xs tracking-wider uppercase px-4 py-2 ${p.color} border border-transparent hover:border-rose-300 transition-all duration-300`}
                  >
                    <span>{p.name}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {error && (
            <p className="text-rose-600 text-center">{t.loadError}: {error}</p>
          )}

          {posts === null && !error && (
            <p className="text-stone-400 text-center">{t.loading}</p>
          )}

          {posts && posts.length === 0 && (
            <div className="max-w-2xl mx-auto text-center bg-white border border-stone-100 shadow-sm p-12">
              <h2 className="font-serif text-2xl text-stone-800 mb-3">
                {t.empty}
              </h2>
            </div>
          )}

          {posts && posts.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p) => {
                const pillarClass = PILLAR_COLORS[p.pillar] || 'bg-stone-100 text-stone-700';
                return (
                  <Link
                    key={p.id}
                    to={`${t.articleBase}/${p.slug}`}
                    data-testid={`article-card-${p.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300"
                  >
                    {p.cover_image ? (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-stone-100 via-rose-50 to-stone-50 flex items-center justify-center">
                        <Sparkles className="w-10 h-10 text-rose-300" />
                      </div>
                    )}
                    <div className="p-6">
                      {p.pillar && (
                        <span className={`inline-block text-[10px] tracking-[0.25em] uppercase px-2.5 py-1 rounded ${pillarClass} mb-3`}>
                          {p.pillar}
                        </span>
                      )}
                      <h2 className="font-serif text-xl text-stone-900 mb-3 leading-snug group-hover:text-rose-500 transition-colors">
                        {p.title}
                      </h2>
                      <p className="text-stone-600 text-sm mb-4 line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                        <div className="text-stone-500 text-xs">
                          <div className="font-medium text-stone-800">{p.author_name}</div>
                          {p.reading_time_min ? (
                            <div className="flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" />
                              {p.reading_time_min} {t.minRead}
                            </div>
                          ) : null}
                        </div>
                        <ArrowRight className="w-4 h-4 text-rose-500 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
